import { supabase } from '../supabase';
import { TAX_BILL_WORKFLOW as W, TAX_BILL_WORKFLOW_LABEL } from '../constants/statuses';

// Thin wrapper over the expected_bills(tax_year) RPC — the SINGLE source for Bubble 1 (Processing worklist) and
// Bubble 2 (Approvals). The SQL already unions expected bills (parcel_collectors-driven) with orphan actual bills
// and maps the display status, so rows arrive ready to use:
//   parcel_id, property_id, client_id, collector_id, installment_number, kind, due_date, ts_id,
//   raw_status, workflow_status ('outstanding'|'not_started'|'processed'|'approved'|'transmitted'|'filed'),
//   tax_bill_id, transmit_bills, orphan.  Keep this thin — the SQL does the work.
export async function loadExpectedBills(taxYear) {
  const { data, error } = await supabase.rpc('expected_bills', { p_tax_year: Number(taxYear) });
  if (error) { console.error('[expected_bills] rpc:', error); return []; }
  return data || [];
}

// "Pull state X into processing" — PARCEL-FIRST view (pull_parcels_by_state RPC). Unlike loadExpectedBills, this
// returns EVERY non-retired parcel in the state (date-independent), incl. collector-less ones carrying a
// coverage_status ('covered' | 'no_collector' | 'collector_no_installments'). Separate function so the owed-worklist
// + Approvals bubbles (which consume expected_bills) are never polluted by uncovered rows.
export async function loadPullByState(state, taxYear) {
  const { data, error } = await supabase.rpc('pull_parcels_by_state', { p_state: state, p_tax_year: Number(taxYear) });
  if (error) { console.error('[pull_parcels_by_state] rpc:', error); return []; }
  return data || [];
}
// Distinct states with (non-retired) parcels — the pull dropdown source.
export async function loadPullStates(taxYear) {
  const { data, error } = await supabase.rpc('parcel_states', { p_tax_year: Number(taxYear) });
  if (error) { console.error('[parcel_states] rpc:', error); return []; }
  return (data || []).map(r => r.state).filter(Boolean);
}

export const workflowLabel = (row) => TAX_BILL_WORKFLOW_LABEL[row?.workflow_status] || row?.workflow_status || '';
export const byStatus = (rows, ...statuses) => rows.filter(r => statuses.includes(r.workflow_status));

// Bubble 1 — Processing worklist: expected bills the TS still owes — not yet processed (Not Started ≤45d, then
// Outstanding) PLUS bills the CS returned (Rejected) that the TS must re-do.
// ⛔ W.OVERDUE MUST BE IN THIS LIST. It is an allow-list, so a bucket missing from it disappears from the
// worklist entirely — and overdue is the most urgent work on it.
export const processingRows = (rows) => byStatus(rows, W.OVERDUE, W.NOT_STARTED, W.OUTSTANDING, W.REJECTED);
// Bubble 2 — Approvals: bills submitted and awaiting CS approval (Rejected has left for the TS worklist).
export const approvalRows = (rows) => byStatus(rows, W.PROCESSED);

// Group rows into property × collector × installment buckets — the TBT / approval grain.
export function groupByPropertyCollector(rows) {
  const m = new Map();
  for (const r of rows) {
    const k = `${r.property_id}|${r.collector_id}|${r.installment_number ?? '-'}`;
    const e = m.get(k) || {
      property_id: r.property_id, client_id: r.client_id, collector_id: r.collector_id,
      installment_number: r.installment_number, transmit_bills: r.transmit_bills, rows: [],
    };
    e.rows.push(r); m.set(k, e);
  }
  return [...m.values()];
}

// Shared nested grouping: property → (collector, installment) sub-card → parcels, with per-sub roll-ups
// (total / withBill / approved / awaiting / orphan) and per-property sums. Returns EVERY property (no queue filter) so
// the two bubbles can apply their own queue predicate. Bubble 2 (Approvals) and Bubble 3 (Transmittals) are thin
// wrappers below — keep the shape stable, both depend on it.
function groupByPropertySubs(rows) {
  const props = new Map();
  for (const r of rows) {
    let p = props.get(r.property_id);
    if (!p) { p = { property_id: r.property_id, client_id: r.client_id, property_name: r.property_name, client_name: r.client_name, state: r.state, subs: new Map() }; props.set(r.property_id, p); }
    const sk = `${r.collector_id}|${r.installment_number ?? '-'}`;
    let sub = p.subs.get(sk);
    if (!sub) { sub = { collector_id: r.collector_id, collector_name: r.collector_name, installment_number: r.installment_number, transmit_bills: r.transmit_bills, parcels: [] }; p.subs.set(sk, sub); }
    sub.parcels.push(r);
  }
  const out = [];
  for (const p of props.values()) {
    const subs = [...p.subs.values()].map(sub => {
      const total = sub.parcels.length;
      const withBill = sub.parcels.filter(x => x.tax_bill_id).length;                      // "Y" — parcels that have a bill
      const approved = sub.parcels.filter(x => x.workflow_status === W.APPROVED).length;
      const awaiting = sub.parcels.filter(x => x.workflow_status === W.PROCESSED).length;   // processed = submitted/flagged, needs CS
      const orphan = sub.parcels.some(x => x.orphan);
      // Parcels ordered by due date, null last (mirror the worklist sort).
      const parcels = [...sub.parcels].sort((a, b) => (Date.parse(a.due_date) || Infinity) - (Date.parse(b.due_date) || Infinity));
      return { ...sub, parcels, total, withBill, approved, awaiting, orphan };
    }).sort((a, b) => (a.collector_name || '').localeCompare(b.collector_name || '') || ((a.installment_number ?? 0) - (b.installment_number ?? 0)));
    out.push({ ...p, subs, awaiting: subs.reduce((n, sub) => n + sub.awaiting, 0), approved: subs.reduce((n, sub) => n + sub.approved, 0) });
  }
  return out;
}

// Bubble 2 — Approvals: properties with ≥1 bill AWAITING CS approval (workflow_status 'processed'). A queued property
// shows its BILLED (collector, installment) sub-cards — an installment with nothing scanned yet (withBill === 0) has
// nothing to approve and is dropped, so a not-yet-received inst-2 doesn't render as a dead "Approved 0/0" card (that
// state is tracked on the TS Processing worklist instead). Within a kept sub, ALL expected parcels still show (incl.
// not_started/outstanding) for "waiting on 3 of 4" context. This sub-filter is SCOPED here, not in the shared
// groupByPropertySubs, because Transmittals consumes that helper too. p.awaiting/p.approved are unchanged by the
// filter (no-bill subs contribute 0 to both), so the queue predicate and the "N awaiting" chip still hold.
export function groupForApprovals(rows) {
  return groupByPropertySubs(rows)
    .map(p => ({ ...p, subs: p.subs.filter(su => su.withBill > 0) }))
    .filter(p => p.awaiting > 0);
}

// Bubble 3 — Transmittals: properties with ≥1 APPROVED bill (cleared the C2a file/transmit fork → transmittal-bound).
// Each (collector, installment) sub with approved > 0 IS one TBT. Returns [] when nothing is approved.
export function groupForTransmittals(rows) {
  return groupByPropertySubs(rows).filter(p => p.approved > 0);
}

// Counts for the bubbles + a data-gap signal (orphans = bill exists but no parcel_collectors selection).
export function bubbleCounts(rows) {
  const c = { outstanding: 0, not_started: 0, processed: 0, approved: 0, transmitted: 0, filed: 0, orphan: 0 };
  for (const r of rows) {
    if (c[r.workflow_status] != null) c[r.workflow_status] += 1;
    if (r.orphan) c.orphan += 1;
  }
  return c;
}
